import { API } from '../config';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import {
  ChevronLeft, Send, Users, User, Hash, RefreshCw, AlertTriangle,
  Search, X, Phone, MoreVertical, Trash2, Pin, BellOff, Archive,
  Image, FileText, Mic, Download, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const ACTIVE_POLL = 2500;  // 2.5s for active chat
const LIST_POLL = 10000;    // 10s for dialog list

export function TelegramUserChats({ selectedAccount = 'all', accounts: parentAccounts = [] }) {
  const { getAuthHeaders, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [dialogs, setDialogs] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  // In-chat search
  const [chatSearch, setChatSearch] = useState('');
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchIdx, setChatSearchIdx] = useState(-1);
  const msgEndRef = useRef(null);
  const prevAccount = useRef(selectedAccount);

  // ===== FETCH =====
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/telegram-user/status`, { headers: getAuthHeaders() });
      setStatus(res.data);
      if (res.data.connected) fetchDialogs();
      else setLoading(false);
    } catch { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  const fetchDialogs = useCallback(async () => {
    try {
      let p = '?limit=50';
      if (selectedAccount === 'all') p += '&all_accounts=true';
      else p += `&session_id=${selectedAccount}`;
      const res = await axios.get(`${API}/telegram-user/dialogs${p}`, { headers: getAuthHeaders() });
      setDialogs(res.data.dialogs || []);
    } catch (e) { console.error('Dialogs:', e); }
    finally { setLoading(false); }
  }, [getAuthHeaders, selectedAccount]);

  const fetchMessages = useCallback(async (chatId, sid) => {
    if (!chatId) return;
    try {
      const p = sid ? `?limit=50&session_id=${sid}` : '?limit=50';
      const res = await axios.get(`${API}/telegram-user/messages/${chatId}${p}`, { headers: getAuthHeaders() });
      const newMsgs = res.data.messages || [];
      setMessages(prev => {
        if (prev.length === 0 || prev.length !== newMsgs.length || prev[prev.length - 1]?.id !== newMsgs[newMsgs.length - 1]?.id) {
          return newMsgs;
        }
        return prev;
      });
    } catch (e) { console.error('Messages:', e); }
  }, [getAuthHeaders]);

  // ===== EFFECTS =====
  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (msgEndRef.current) msgEndRef.current.scrollIntoView({ behavior: 'auto' }); }, [messages]);

  // Fast polling for active chat
  useEffect(() => {
    if (!selectedChat || !mobileShowChat) return;
    const t = setInterval(() => fetchMessages(selectedChat.id, chatSessionId), ACTIVE_POLL);
    return () => clearInterval(t);
  }, [selectedChat, mobileShowChat, chatSessionId, fetchMessages]);

  // Dialog list polling
  useEffect(() => {
    if (!status?.connected) return;
    const t = setInterval(fetchDialogs, LIST_POLL);
    return () => clearInterval(t);
  }, [status?.connected, fetchDialogs]);

  // Account switch
  useEffect(() => {
    if (prevAccount.current !== selectedAccount) {
      prevAccount.current = selectedAccount;
      setSelectedChat(null); setChatSessionId(null); setMessages([]); setMobileShowChat(false);
      if (status?.connected) { setLoading(true); fetchDialogs(); }
    }
  }, [selectedAccount, status?.connected, fetchDialogs]);

  // ===== HANDLERS =====
  const openChat = useCallback((dialog) => {
    const sid = dialog.session_id || (selectedAccount !== 'all' ? selectedAccount : null);
    setSelectedChat(dialog); setChatSessionId(sid); setMessages([]); setMobileShowChat(true);
    setShowSearchResults(false); setSearchQuery(''); setShowChatSearch(false); setChatSearch('');
    fetchMessages(dialog.id, sid);
  }, [selectedAccount, fetchMessages]);

  const closeChat = useCallback(() => {
    setMobileShowChat(false); setSelectedChat(null); setChatSessionId(null); setMessages([]);
    setShowChatSearch(false); setChatSearch('');
    fetchDialogs();
  }, [fetchDialogs]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending || !selectedChat) return;
    setSending(true); const text = newMessage; setNewMessage('');
    try {
      await axios.post(`${API}/telegram-user/send`, { chat_id: selectedChat.id, text, session_id: chatSessionId }, { headers: getAuthHeaders() });
      await fetchMessages(selectedChat.id, chatSessionId);
    } catch { toast.error('Mesaj gönderilemedi'); setNewMessage(text); }
    finally { setSending(false); }
  };

  const handleDelete = async (msgId) => {
    if (!selectedChat) return;
    try {
      await axios.post(`${API}/telegram-user/delete-message`, { chat_id: selectedChat.id, message_id: msgId, session_id: chatSessionId }, { headers: getAuthHeaders() });
      toast.success('Mesaj silindi'); setMessages(p => p.filter(m => m.id !== msgId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Mesaj silinemedi'); }
  };

  const handleDeleteChat = async () => {
    if (!selectedChat || !window.confirm('Bu sohbeti silmek istediğinize emin misiniz?')) return;
    try {
      await axios.post(`${API}/telegram-user/delete-chat`, { chat_id: selectedChat.id, session_id: chatSessionId }, { headers: getAuthHeaders() });
      toast.success('Sohbet silindi'); closeChat();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sohbet silinemedi'); }
  };

  const handleChatAction = async (action) => {
    if (!selectedChat) return;
    try {
      await axios.post(`${API}/telegram-user/${action}`, { chat_id: selectedChat.id, session_id: chatSessionId }, { headers: getAuthHeaders() });
      const msgs = { 'pin-chat': 'Sabitlendi', 'unpin-chat': 'Sabitleme kaldırıldı', 'mute-chat': 'Sessize alındı', 'unmute-chat': 'Ses açıldı', 'archive-chat': 'Arşivlendi' };
      toast.success(msgs[action] || 'Tamam'); fetchDialogs();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true); setShowSearchResults(true);
    try {
      const res = await axios.get(`${API}/telegram-user/search?q=${encodeURIComponent(searchQuery)}${chatSessionId ? `&session_id=${chatSessionId}` : ''}`, { headers: getAuthHeaders() });
      setSearchResults(res.data.results || []);
    } catch { toast.error('Arama başarısız'); }
    finally { setIsSearching(false); }
  };

  // In-chat search
  const chatSearchMatches = chatSearch ? messages.filter(m => m.text?.toLowerCase().includes(chatSearch.toLowerCase())).map(m => m.id) : [];
  const navigateChatSearch = (dir) => {
    if (chatSearchMatches.length === 0) return;
    let next = chatSearchIdx + dir;
    if (next < 0) next = chatSearchMatches.length - 1;
    if (next >= chatSearchMatches.length) next = 0;
    setChatSearchIdx(next);
    document.getElementById(`msg-${chatSearchMatches[next]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Media URL builder
  const mediaUrl = (chatId, msgId, sid) => `${API}/telegram-user/media/${chatId}/${msgId}?session_id=${sid || ''}&token=${token}`;

  // ===== HELPERS =====
  const ini = (n) => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  const ac = (n) => { const c = ['bg-blue-600','bg-emerald-600','bg-amber-600','bg-rose-600','bg-violet-600','bg-cyan-600','bg-orange-600','bg-teal-600']; let h = 0; for (let i = 0; i < (n||'').length; i++) h = n.charCodeAt(i) + ((h << 5) - h); return c[Math.abs(h) % c.length]; };
  const ci = (t) => t === 'group' ? Users : t === 'channel' ? Hash : User;
  const ft = (ts) => { if (!ts) return ''; const d = new Date(ts); const diff = Date.now() - d; if (diff < 60000) return 'şimdi'; if (diff < 3600000) return `${Math.floor(diff / 60000)}dk`; if (diff < 86400000) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); return d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }); };
  const fmt = (ts) => ts ? new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '';
  const fmtSize = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : b > 1024 ? `${(b/1024).toFixed(0)} KB` : `${b} B`;

  // ===== NOT CONNECTED =====
  if (!loading && !status?.connected) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-sm font-medium text-foreground mb-1">
            {status?.status === 'not_configured' ? 'Telegram kullanıcı girişi yapılandırılmamış' : 'Telegram hesabı bağlı değil'}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {status?.status === 'not_configured' ? 'Bu özellik henüz etkinleştirilmemiş.' : 'Kişisel mesajlarınızı görmek için hesabınızı bağlayın.'}
          </p>
          {status?.available && <Button variant="outline" className="rounded-xl text-xs" onClick={() => window.location.href = '/app/telegram-user-login'}><Phone className="w-3 h-3 mr-1.5" /> Telegram Hesabı Bağla</Button>}
        </div>
      </div>
    );
  }
  if (loading) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RefreshCw className="w-5 h-5 animate-spin text-primary" /></div>;

  // ===== RENDER =====
  return (
    <div style={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* LEFT */}
      <div style={{ display: mobileShowChat ? 'none' : 'flex', flexDirection: 'column', width: '100%', maxWidth: 380, height: '100%', borderRight: '1px solid hsl(var(--border))', flexShrink: 0 }} className="lg:!flex">
        <div style={{ flexShrink: 0, padding: '8px 12px', borderBottom: '1px solid hsl(var(--border))' }}>
          {selectedAccount === 'all' && parentAccounts.length > 1 && <p className="text-[10px] text-muted-foreground mb-1">Tüm Hesaplar ({parentAccounts.length})</p>}
          {selectedAccount !== 'all' && <p className="text-[10px] text-primary font-medium mb-1">Aktif Hesap: {(() => { const a = parentAccounts.find(x => x.id === selectedAccount); return a ? `@${a.tg_username || a.tg_first_name || '?'}` : ''; })()}</p>}
          <div className="flex gap-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doSearch(); if (e.key === 'Escape') { setShowSearchResults(false); setSearchQuery(''); } }} placeholder="Kullanıcı veya sohbet ara..." className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-background border border-border/70 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40" />
            </div>
            {searchQuery && <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }} className="p-1.5 rounded-lg hover:bg-secondary/50"><X className="w-4 h-4 text-muted-foreground" /></button>}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          {showSearchResults ? (
            <>{isSearching ? <p className="p-4 text-center text-sm text-muted-foreground">Aranıyor...</p> : searchResults.length === 0 ? <p className="p-4 text-center text-sm text-muted-foreground">Sonuç bulunamadı</p> : searchResults.map(r => <DialogRow key={r.id} d={{...r, last_message: r.username ? `@${r.username}` : '', unread_count: 0, last_date: ''}} onClick={() => openChat(r)} />)}</>
          ) : dialogs.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Sohbet bulunamadı</p> : dialogs.map(d => <DialogRow key={`${d.id}-${d.session_id||''}`} d={d} onClick={() => openChat(d)} selected={selectedChat?.id === d.id} />)}
        </div>
        <div style={{ flexShrink: 0, padding: '4px 12px', borderTop: '1px solid hsl(var(--border))' }} className="flex items-center justify-between">
          <Badge variant="secondary" className="text-[10px]">Telegram</Badge>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] rounded-lg" onClick={() => { setLoading(true); fetchDialogs(); }}><RefreshCw className="w-3 h-3 mr-1" /> Yenile</Button>
        </div>
      </div>

      {/* RIGHT */}
      <div style={{ display: !mobileShowChat ? 'none' : 'flex', flexDirection: 'column', flex: 1, height: '100%', minWidth: 0 }} className="lg:!flex">
        {selectedChat ? (
          <>
            {/* Header */}
            <div style={{ flexShrink: 0, padding: '8px 12px', borderBottom: '1px solid hsl(var(--border))' }} className="bg-card flex items-center gap-2">
              <Button variant="ghost" size="icon" className="lg:hidden rounded-xl flex-shrink-0 h-8 w-8" onClick={closeChat}><ChevronLeft className="w-5 h-5" /></Button>
              <Avatar className="w-8 h-8 flex-shrink-0"><AvatarFallback className={`${ac(selectedChat.name)} text-white text-[10px]`}>{ini(selectedChat.name)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-sm truncate text-foreground">{selectedChat.name}</h2>
                <Badge variant="secondary" className="text-[9px]">{selectedChat.chat_type === 'group' ? 'Grup' : selectedChat.chat_type === 'channel' ? 'Kanal' : 'Özel'}</Badge>
              </div>
              <Button variant="ghost" size="icon" className="rounded-xl h-8 w-8 flex-shrink-0" onClick={() => setShowChatSearch(!showChatSearch)}><Search className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="rounded-xl h-8 w-8 flex-shrink-0" onClick={() => fetchMessages(selectedChat.id, chatSessionId)}><RefreshCw className="w-3.5 h-3.5" /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="rounded-xl h-8 w-8 flex-shrink-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuItem onClick={() => handleChatAction(selectedChat.is_pinned ? 'unpin-chat' : 'pin-chat')}><Pin className="w-3 h-3 mr-2" /> {selectedChat.is_pinned ? 'Sabitlemeyi Kaldır' : 'Sohbeti Sabitle'}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleChatAction('mute-chat')}><BellOff className="w-3 h-3 mr-2" /> Sessize Al</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleChatAction('archive-chat')}><Archive className="w-3 h-3 mr-2" /> Arşivle</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDeleteChat} className="text-destructive"><Trash2 className="w-3 h-3 mr-2" /> Sohbeti Sil</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* In-chat search */}
            {showChatSearch && (
              <div style={{ flexShrink: 0, padding: '6px 12px', borderBottom: '1px solid hsl(var(--border))' }} className="bg-secondary/20 flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <input value={chatSearch} onChange={e => { setChatSearch(e.target.value); setChatSearchIdx(-1); }} placeholder="Sohbette ara..." autoFocus
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
                {chatSearchMatches.length > 0 && <span className="text-[10px] text-muted-foreground">{chatSearchIdx + 1}/{chatSearchMatches.length}</span>}
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded" onClick={() => navigateChatSearch(-1)} disabled={chatSearchMatches.length === 0}><ChevronUp className="w-3 h-3" /></Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded" onClick={() => navigateChatSearch(1)} disabled={chatSearchMatches.length === 0}><ChevronDown className="w-3 h-3" /></Button>
                <button onClick={() => { setShowChatSearch(false); setChatSearch(''); setChatSearchIdx(-1); }}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>
              </div>
            )}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '12px 16px', WebkitOverflowScrolling: 'touch' }}>
              {messages.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <>{messages.map(msg => {
                  const isHighlighted = chatSearch && chatSearchMatches.includes(msg.id);
                  const isActive = chatSearchMatches[chatSearchIdx] === msg.id;
                  return (
                    <div key={msg.id} id={`msg-${msg.id}`} className={`flex mb-2 group ${msg.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] px-3 py-2 rounded-2xl relative ${
                        msg.direction === 'out' ? 'bg-[hsl(184,58%,18%)] text-[hsl(198,50%,96%)]' : 'bg-[hsl(222,14%,14%)] text-foreground border border-border/60'
                      } ${isActive ? 'ring-2 ring-yellow-500' : isHighlighted ? 'ring-1 ring-yellow-500/50' : ''}`}>
                        {msg.direction === 'in' && <p className="text-xs font-medium text-primary mb-0.5">{msg.sender_name}</p>}
                        {/* Media */}
                        {msg.media && msg.media.type === 'photo' && (
                          <div className="mb-1 rounded-lg overflow-hidden bg-secondary/30">
                            <img src={mediaUrl(selectedChat.id, msg.id, chatSessionId)} alt="Fotoğraf" className="max-w-full max-h-64 object-contain rounded-lg" loading="lazy"
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                            <div className="hidden items-center gap-1.5 p-2 text-xs text-muted-foreground"><Image className="w-3.5 h-3.5" /> Fotoğraf yüklenemedi</div>
                          </div>
                        )}
                        {msg.media && msg.media.type === 'voice' && (
                          <div className="flex items-center gap-2 mb-1 bg-secondary/20 rounded-lg p-2">
                            <Mic className="w-4 h-4 text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground flex-1">Sesli mesaj</span>
                            <a href={mediaUrl(selectedChat.id, msg.id, chatSessionId)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline">Oynat</a>
                          </div>
                        )}
                        {msg.media && msg.media.type === 'document' && (
                          <div className="flex items-center gap-2 mb-1 bg-secondary/20 rounded-lg p-2">
                            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs truncate text-foreground">{msg.media.filename || 'Dosya'}</p>
                              {msg.media.size > 0 && <p className="text-[10px] text-muted-foreground">{fmtSize(msg.media.size)}</p>}
                            </div>
                            <a href={mediaUrl(selectedChat.id, msg.id, chatSessionId)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-0.5"><Download className="w-3 h-3" /> İndir</a>
                          </div>
                        )}
                        {/* Text */}
                        {msg.text && msg.text !== '[Medya]' && <p className="text-[15px] leading-6 whitespace-pre-wrap break-words">{chatSearch ? highlightText(msg.text, chatSearch) : msg.text}</p>}
                        {/* Footer: time + read + delete */}
                        <div className="flex items-center justify-end gap-1 mt-0.5">
                          <span className={`text-[10px] ${msg.direction === 'out' ? 'text-[hsl(198,30%,70%)]' : 'text-muted-foreground'}`}>{fmt(msg.timestamp)}</span>
                          {msg.direction === 'out' && msg.read_status && <span className={`text-[9px] ${msg.read_status === 'read' ? 'text-green-400' : 'text-[hsl(198,30%,60%)]'}`}>{msg.read_status === 'read' ? '✓✓' : '✓'}</span>}
                          <button onClick={() => handleDelete(msg.id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 p-0.5 rounded hover:bg-destructive/20" title="Mesajı sil"><Trash2 className="w-3 h-3 text-destructive/70" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}<div ref={msgEndRef} /></>
              )}
            </div>

            {/* Composer */}
            <div style={{ flexShrink: 0, padding: '8px 12px', borderTop: '1px solid hsl(var(--border))' }} className="bg-card">
              <form onSubmit={handleSend} className="flex items-end gap-2">
                <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={handleKeyDown} placeholder="Bir mesaj yazın..." rows={1}
                  style={{ minHeight: 40, maxHeight: 120 }}
                  className="flex-1 resize-none rounded-xl border border-border/70 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                <Button type="submit" size="icon" className="rounded-xl flex-shrink-0" disabled={!newMessage.trim() || sending} style={{ height: 40, width: 40 }}><Send className="w-4 h-4" /></Button>
              </form>
            </div>
          </>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}><User className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" /><p className="text-muted-foreground text-sm">Mesajlaşmaya başlamak için bir sohbet seçin</p></div>
          </div>
        )}
      </div>
    </div>
  );
}

// Dialog Row
function DialogRow({ d, onClick, selected = false }) {
  const Icon = d.chat_type === 'group' ? Users : d.chat_type === 'channel' ? Hash : User;
  const ini = (n) => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  const ac = (n) => { const c = ['bg-blue-600','bg-emerald-600','bg-amber-600','bg-rose-600','bg-violet-600','bg-cyan-600','bg-orange-600','bg-teal-600']; let h = 0; for (let i = 0; i < (n||'').length; i++) h = n.charCodeAt(i) + ((h << 5) - h); return c[Math.abs(h) % c.length]; };
  const ft = (ts) => { if (!ts) return ''; const dd = new Date(ts); const diff = Date.now() - dd; if (diff < 60000) return 'şimdi'; if (diff < 3600000) return `${Math.floor(diff / 60000)}dk`; if (diff < 86400000) return dd.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); return dd.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }); };
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 text-left border-b border-border/20 ${selected ? 'bg-secondary/70' : ''}`}>
      <Avatar className="w-10 h-10 flex-shrink-0"><AvatarFallback className={`${ac(d.name)} text-white text-xs font-medium`}>{ini(d.name)}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm truncate text-foreground flex items-center gap-1">
            {d.is_pinned && <Pin className="w-3 h-3 text-primary flex-shrink-0" />}
            <Icon className="w-3 h-3 text-muted-foreground flex-shrink-0" /> {d.name}
          </span>
          <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{ft(d.last_date)}</span>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-xs text-muted-foreground truncate">
            {d.account_label && <span className="text-primary text-[10px] mr-1">{d.account_label}</span>}
            {d.last_message || ''}
          </p>
          {(d.unread_count > 0) && <Badge className="ml-1 h-4 min-w-4 text-[10px] px-1 bg-primary text-primary-foreground rounded-full flex-shrink-0">{d.unread_count}</Badge>}
        </div>
      </div>
    </button>
  );
}

function highlightText(text, keyword) {
  if (!keyword) return text;
  const parts = text.split(new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((p, i) => p.toLowerCase() === keyword.toLowerCase() ? <mark key={i} className="bg-yellow-500/30 text-inherit rounded px-0.5">{p}</mark> : p);
}
